import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, CircularProgress, Button, Grid, Modal, Box,
  TextField, Select, MenuItem, FormControl, InputLabel, Fab, Snackbar, Alert,
  useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions,
  Container, Paper, IconButton
} from '@mui/material';
import { KeyboardArrowUp as UpIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './mobileModal.css';   // 新增這行

// 下拉選項列表（將從 API 動態載入）
let communityOptions = [];
let departmentOptions = [];
let positionOptions = [];

export default function ProductListPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  // 記錄每個 productId 是否已報名
  const [registeredMap, setRegisteredMap] = useState({});
  const [formVisible, setFormVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    community: '',
    department: '', 
    position: '',
    name: '',
    mobile: '',
    other: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [showScroll, setShowScroll] = useState(false);
  const [basicDataOptions, setBasicDataOptions] = useState({
    community: [],
    department: [],
    position: []
  });
  // Scroll-to-top functionality
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // lg以上才是電腦版
  const isMobile = useMediaQuery(theme.breakpoints.down('lg')); // lg以下(xs, sm, md)都是手機版
  // 手機版 (xs, sm, md) 放大字體，電腦版 (lg以上) 用正常大小
  const fontSize = (base) => isMobile ? `calc(${base} * 1.5)` : base;
  const navigate = useNavigate();

  // 載入基本檔資料選項
  const fetchBasicDataOptions = async () => {
    try {
      const dataTypes = ['community', 'department', 'position'];
      const promises = dataTypes.map(async (type) => {
        const response = await fetch(`/api/basic-data/${type}`, { credentials: 'include' });
        const data = await response.json();
        return { type, data };
      });
      
      const results = await Promise.all(promises);
      const newOptions = {};
      results.forEach(({ type, data }) => {
        newOptions[type] = data;
      });
      
      setBasicDataOptions(newOptions);
      
      // 設定表單預設值為排序後的第一個項目
      setFormData(prev => ({
        ...prev,
        community: newOptions.community[0] || '',
        department: newOptions.department[0] || '',
        position: newOptions.position[0] || ''
      }));
      
      // 更新全局選項變數以供 Select 組件使用
      communityOptions.length = 0;
      communityOptions.push(...newOptions.community);
      departmentOptions.length = 0;
      departmentOptions.push(...newOptions.department);
      positionOptions.length = 0;
      positionOptions.push(...newOptions.position);
    } catch (error) {
      setSnackbar({ open: true, message: '載入基本檔資料失敗', severity: 'error' });
    }
  };

  // 初始載入時檢查登入狀態和基本檔資料
  useEffect(() => {
    checkAuthStatus().then(setUser);
    fetchBasicDataOptions();
  }, []);
  useEffect(() => {
    console.log('useEffect 觸發，user:', user);
    fetch('/api/products', { cache: 'no-store' })
      .then(res => {
        console.log('API 回應狀態:', res.status);
        return res.json();
      })
      .then(data => { 
        console.log('原始產品數據:', data);
        // 只顯示啟用且在有效時間內的活動
        const now = new Date();
        console.log('當前時間:', now);
        const activeProducts = data.filter(product => {
          console.log('檢查產品:', product.name, {
            isActive: product.isActive,
            startTime: product.startTime,
            endTime: product.endTime,
            endTimeCheck: product.endTime ? new Date(product.endTime) < now : false
          });
          if (!product.isActive) return false;
          if (product.endTime && new Date(product.endTime) < now) return false;
          return true;
        });
        console.log('過濾後的活動產品:', activeProducts);
        setProducts(activeProducts);
        setLoading(false);
        
        // 檢查 URL 參數是否需要自動開啟報名表單
        const urlParams = new URLSearchParams(window.location.search);
        const openFormProductId = urlParams.get('openForm');
        if (openFormProductId) {
          const product = data.find(p => p.id === parseInt(openFormProductId));
          if (product) {
            setSelectedProduct(product);
            setFormVisible(true);
            // 清除 URL 參數
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
        // 只在用戶已登入時檢查報名狀態
        if (user) {
          activeProducts.forEach(product => {
            fetch(`/api/orders/check?productId=${product.id}`, { credentials: 'include' })
              .then(res => {
                if (res.ok) {
                  return res.json();
                }
                throw new Error('Check failed');
              })
              .then(r => setRegisteredMap(prev => ({ ...prev, [product.id]: r.registered })))
              .catch(() => setRegisteredMap(prev => ({ ...prev, [product.id]: false })));
          });
        } else {
          // 用戶未登入時，將所有活動標記為未報名
          activeProducts.forEach(product => {
            setRegisteredMap(prev => ({ ...prev, [product.id]: false }));
          });
        }
      })
      .catch(error => {
        console.error('獲取產品數據失敗:', error);
        setLoading(false);
        setSnackbar({ open: true, message: '載入活動資料失敗', severity: 'error' });
      });
  }, []);

  // 取得已報名活動（僅在用戶已登入時）
  useEffect(() => {
    if (user) {
      fetch('/api/orders', { credentials: 'include' })
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          throw new Error('Failed to fetch orders');
        })
        .then(data => {
          // 確保回傳為陣列
          const orders = Array.isArray(data) ? data : [];
          const ids = new Set();
          orders.forEach(o => o.orderItems.forEach(oi => ids.add(oi.productId)));
          setRegisteredMap(prev => {
            const newMap = { ...prev };
            ids.forEach(id => { newMap[id] = true; });
            return newMap;
          });
        })
        .catch(() => {
          // 靜默處理錯誤
          setRegisteredMap({});
        });
    }
  }, [user]);
  // Listen to window scroll to toggle scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 檢查登入狀態（驗證後端 session）
  const checkAuthStatus = async () => {
    try {
      const res = await fetch('/api/auth/status', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          // 同步更新 localStorage
          localStorage.setItem('user', JSON.stringify(data.user));
          return data.user;
        }
      }
      // 後端 session 無效或用戶未登入，清除前端快取
      localStorage.removeItem('user');
      return null;
    } catch (e) {
      // 靜默處理錯誤，前台頁面不要求登入
      localStorage.removeItem('user');
      return null;
    }
  };

  // 檢查報名狀態並決定下一步動作
  const handleRegisterClick = async (product) => {
    // 先驗證登入狀態
    const user = await checkAuthStatus();
    if (!user) {
      // 未登入或 session 失效：導向 LINE 登入流程
      navigate(`/login?productId=${product.id}`);
    } else {
      // 已登入且 session 有效：開啟報名表單
      setSelectedProduct(product);
      setFormVisible(true);
    }
  };

  const handleRegister = async () => {
    if (!selectedProduct) return;
    
    // 檢查庫存
    if (selectedProduct.stock === 0) {
      setSnackbar({ open: true, message: '活動已額滿，無法報名', severity: 'error' });
      return;
    }
    
    // 檢查報名截止時間
    if (selectedProduct.registrationEnd && new Date(selectedProduct.registrationEnd) < new Date()) {
      setSnackbar({ open: true, message: '報名時間已截止', severity: 'error' });
      return;
    }
    
    // 驗證必填欄位
    const errors = {};
    if (!formData.community) errors.community = '請選擇社團';
    if (!formData.department) errors.department = '請選擇社別/會別';
    if (!formData.position) errors.position = '請選擇社職/會職';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    const values = formData;
    try {
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items: [{ id: selectedProduct.id, quantity: 1, price: selectedProduct.price }], extra: values })
      });
      if (res.ok) {
        const order = await res.json();
        setSnackbar({ open: true, message: '報名成功', severity: 'success' });
        setRegisteredMap(prev => ({ ...prev, [selectedProduct.id]: true }));
        
        // 檢查是否需要提醒用戶加入 Bot 好友
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const lineId = user.lineId;
        
        if (lineId) {
          try {
            // 檢查電子票發送狀態
            const ticketRes = await fetch('/api/line/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ 
                lineId, 
                message: '🎫 您的電子票已準備完成！',
                order: order 
              })
            });
            
            const ticketResult = await ticketRes.json();
            
            if (!ticketResult.ok || ticketResult.error) {
              // 電子票發送失敗，可能是未加好友
              setSnackbar({ 
                open: true, 
                message: `報名成功！電子票發送失敗，請先加入 LINE Bot 好友`, 
                severity: 'warning' 
              });
            } else {
              // 電子票發送成功
              const thumbnailImageUrl = `https://app.li.com.tw/api/qrcode/${order.id}.png`;
              const template = {
                type: 'buttons',
                thumbnailImageUrl,
                imageAspectRatio: 'rectangle',
                imageSize: 'contain',
                imageBackgroundColor: '#FFFFFF',
                title: '這是你專屬門票',
                text: '請出示此門票入場',
                actions: [
                  {
                    type: 'uri',
                    label: '新增到 Google 日曆',
                    uri: 'https://calendar.google.com/calendar/u/0/r/eventedit?text=%E9%9B%B2%E6%9E%97+IMC+%E5%A4%A7%E5%9E%8B%E6%BC%94%E8%AC%9B%E6%B4%BB%E5%8B%95&dates=20250823T053000Z/20250823T083000Z'
                  }
                ]
              };
              await fetch('/api/line/template', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ lineId, altText: '你的雲林IMC電子票.', template })
              });
              
              setSnackbar({ 
                open: true, 
                message: `🎉 報名成功！電子票已發送到您的 LINE`, 
                severity: 'success' 
              });
            }
          } catch (e) {
            console.error('電子票狀態檢查失敗', e);
            // 如果檢查失敗，顯示通用的成功訊息和加好友提醒
            setSnackbar({ 
              open: true, 
              message: `報名成功！如未收到電子票請加入 LINE Bot 好友`, 
              severity: 'info' 
            });
          }
        }
        setFormVisible(false);
        // 報名成功後導回首頁
        window.location.href = '/';
      } else {
        const err = await res.json();
        setSnackbar({ open: true, message: '報名失敗: ' + (err.error || JSON.stringify(err)), severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: '報名錯誤: ' + e.message, severity: 'error' });
    }
  };
  // 處理登出，呼叫後端登出 API 清除 session cookie
  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.error('Logout API failed', e);
    }
    localStorage.removeItem('user');
    setUser(null);
    window.location.reload();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div style={{ width: '100vw', minHeight: '100vh', background: '#f5f7fa', position: 'relative' }}>
      {/* 頂部選單列 */}
      <Box
        sx={{
          width: '100%',
          height: isMobile ? '3.5rem' : 56,
          background: '#111',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: isMobile ? '0 2vw' : '0 24px'
        }}
      >
        {user ? (
          <>
            <Typography 
              sx={{ 
                color: '#fff', 
                fontWeight: 600, 
                fontSize: fontSize('1rem'), 
                marginRight: 1 
              }}
            >
              {user.name || user.email || '已登入'}
            </Typography>
            {/* 管理員和簽到人員顯示管理頁（與登出同層） */}
            {(user.role && ['管理者', '最高權限', '簽到人員'].includes(user.role.name || user.role)) && (
              <Button
                sx={{ 
                  color: '#fff', 
                  fontWeight: 600, 
                  fontSize: fontSize('1rem'), 
                  marginRight: 1,
                  textTransform: 'none'
                }}
                onClick={() => window.open('https://app.li.com.tw/admin/products', '_self')}
              >
                管理頁
              </Button>
            )}
            <Button
              sx={{ 
                color: '#fff', 
                fontWeight: 600, 
                fontSize: fontSize('1rem'),
                textTransform: 'none' 
              }}
              onClick={handleLogout}
            >
              登出
            </Button>
          </>
        ) : (
          <Button
            sx={{ 
              color: '#fff', 
              fontWeight: 600, 
              fontSize: fontSize('1rem'),
              textTransform: 'none'
            }}
            onClick={() => navigate('/login')}
          >
            登入
          </Button>
        )}
      </Box>

      <Dialog
        open={formVisible}
        onClose={() => setFormVisible(false)}
        maxWidth={isDesktop ? 'sm' : false}
        fullScreen={!isDesktop}
        PaperProps={{
          sx: {
            width: isDesktop ? 640 : '100vw',
            maxWidth: isDesktop ? 640 : '100vw'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: fontSize('1.25rem') }}>
          {selectedProduct?.name}
        </DialogTitle>
        <DialogContent sx={{ padding: isDesktop ? 3 : 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth error={!!formErrors.community}>
              <InputLabel sx={{ fontSize: fontSize('1.25rem') }}>社團 *</InputLabel>
              <Select
                value={formData.community}
                onChange={(e) => handleFormChange('community', e.target.value)}
                sx={{ fontSize: fontSize('1.25rem') }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {communityOptions.map(opt => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: fontSize('1.25rem') }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.community && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.community}
                </Typography>
              )}
            </FormControl>
            
            <FormControl fullWidth error={!!formErrors.department}>
              <InputLabel sx={{ fontSize: fontSize('1.25rem') }}>社別/會別 *</InputLabel>
              <Select
                value={formData.department}
                onChange={(e) => handleFormChange('department', e.target.value)}
                sx={{ fontSize: fontSize('1.25rem') }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {departmentOptions.map(opt => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: fontSize('1.25rem') }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.department && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.department}
                </Typography>
              )}
            </FormControl>
            
            <FormControl fullWidth error={!!formErrors.position}>
              <InputLabel sx={{ fontSize: fontSize('1.25rem') }}>社職/會職 *</InputLabel>
              <Select
                value={formData.position}
                onChange={(e) => handleFormChange('position', e.target.value)}
                sx={{ fontSize: fontSize('1.25rem') }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {positionOptions.map(opt => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: fontSize('1.25rem') }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.position && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.position}
                </Typography>
              )}
            </FormControl>
            
            <TextField
              label="姓名"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              InputProps={{
                sx: { fontSize: fontSize('1.25rem') }
              }}
              InputLabelProps={{
                sx: { fontSize: fontSize('1.25rem') }
              }}
            />
            
            <TextField
              label="手機"
              value={formData.mobile}
              onChange={(e) => handleFormChange('mobile', e.target.value)}
              fullWidth
              InputProps={{
                sx: { fontSize: fontSize('1.25rem') }
              }}
              InputLabelProps={{
                sx: { fontSize: fontSize('1.25rem') }
              }}
            />
            
            <TextField
              label="備註"
              value={formData.other}
              onChange={(e) => handleFormChange('other', e.target.value)}
              fullWidth
              multiline
              rows={2}
              InputProps={{
                sx: { fontSize: fontSize('1.25rem') }
              }}
              InputLabelProps={{
                sx: { fontSize: fontSize('1.25rem') }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: isDesktop ? 3 : 2, paddingTop: 1 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleRegister}
            sx={{
              fontSize: fontSize('1.25rem'),
              padding: isMobile ? '1.25rem 0' : '1rem 0',
              height: '4.5rem',
            }}
          >
            提交報名
          </Button>
        </DialogActions>
      </Dialog>

      {/* 活動列表 */}
      <Container
        maxWidth={false}
        sx={{
          maxWidth: isMobile ? '100vw' : 1280,
          margin: isMobile ? 0 : '60px auto',
          background: '#fff',
          borderRadius: 2,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          padding: isMobile ? 0.5 : 4
        }}
      >
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              xl: 'repeat(4, 1fr)'
            },
            gap: isMobile ? 1.5 : 3
          }}
        >
          {products.length === 0 && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="h6" textAlign="center" sx={{ py: 4 }}>
                目前沒有可用的活動
              </Typography>
            </Box>
          )}
          {products.map(product => (
            <Box key={product.id}>
              <Paper
                elevation={2}
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '3/4',
                  overflow: 'hidden',
                  borderRadius: isMobile ? 1.5 : 1,
                  backgroundImage: product.imageUrl ? `url("${product.imageUrl}")` : 'none',
                  backgroundColor: product.imageUrl ? 'transparent' : '#fff',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Card
                    sx={{
                      borderRadius: isMobile ? '0 0 12px 12px' : '0 0 8px 8px',
                      background: 'transparent',
                      boxShadow: 'none'
                    }}
                  >
                    <CardContent
                      sx={{
                        padding: 0,
                        background: 'rgba(0,0,0,0.35)',
                        borderRadius: isMobile ? '0 0 12px 12px' : '0 0 8px 8px',
                        p: isMobile ? 2.5 : 2
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          marginBottom: isMobile ? 1.5 : 1,
                          textAlign: 'center',
                          fontSize: fontSize('1rem'),
                          color: '#fff',
                          fontWeight: 600
                        }}
                      >
                        {product.name}
                      </Typography>
                      <Box sx={{ textAlign: 'center', margin: isMobile ? '28px 0 12px' : '24px 0 8px' }}>
                        {product.stock === 0 ? (
                          <Button
                            variant="contained"
                            disabled
                            sx={{
                              fontSize: fontSize('1rem'),
                              whiteSpace: 'nowrap',
                              padding: '0.5rem 1rem',
                              height: '4rem',
                              lineHeight: fontSize('1rem'),
                              textTransform: 'none',
                              backgroundColor: '#9e9e9e',
                              color: '#ffffff'
                            }}
                          >
                            已額滿
                          </Button>
                        ) : registeredMap[product.id] ? (
                          <Button
                            variant="contained"
                            sx={{
                              fontSize: fontSize('1rem'),
                              color: '#ffffff',
                              backgroundColor: '#1976d2',
                              whiteSpace: 'nowrap',
                              padding: '0.5rem 1rem',
                              height: '4rem',
                              lineHeight: fontSize('1rem'),
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: '#1565c0'
                              }
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              window.open('https://line.me/R/ti/p/@eew2351g', '_blank');
                            }}
                          >
                            你已報名,選擇取票
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            sx={{
                              fontSize: fontSize('1rem'),
                              whiteSpace: 'nowrap',
                              padding: '0.5rem 1rem',
                              height: '4rem',
                              lineHeight: fontSize('1rem'),
                              textTransform: 'none'
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handleRegisterClick(product);
                            }}
                          >
                            報名
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* 回頂按鈕 */}
      {showScroll && (
        <Fab
          color="primary"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            right: 24,
            top: 24,
            zIndex: 1000
          }}
        >
          <UpIcon />
        </Fab>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 如不使用購物車功能，可移除以上行動裝置購物車按鈕區塊 */}
    </div>
  );
}
